<!-- Vue 3 wrapper for Bryntum TabPanel -->

<template>
    <div ref="refElement">

    </div>
</template>

<script>
import { getCurrentInstance, onBeforeUnmount, onMounted, ref, watch, provide, toRaw } from 'vue';
import { Base, Container, Layout, Model, Panel, Rectangle, Scroller, StateProvider, TabPanel, Tool, Widget } from '@bryntum/schedulerpro';

import useWrapperHelper from './WrapperHelper.js';
import useWrapperHelperVue3 from './WrapperHelperVue3.js';

export default {
    // eslint-disable-next-line vue/multi-word-component-names
    name : 'BryntumTabPanel',

    props : {

         // Configs only
        adopt                   : { type : [HTMLElement, String], default : undefined },
        align                   : { type : [Object, String], default : undefined },
        anchor                  : { type : Boolean, default : undefined },
        animateTabChange        : { type : Boolean, default : undefined },
        ariaDescription         : { type : String, default : undefined },
        ariaLabel               : { type : String, default : undefined },
        autoHeight              : { type : Boolean, default : undefined },
        autoUpdateRecord        : { type : Boolean, default : undefined },
        bbar                    : { type : [Array, Object], default : undefined },
        bodyCls                 : { type : [String, Object], default : undefined },
        bubbleEvents            : { type : Object, default : undefined },
        callOnFunctions         : { type : Boolean, default : undefined },
        centered                : { type : Boolean, default : undefined },
        collapsed               : { type : Boolean, default : undefined },
        collapsible             : { type : [Boolean, Object], default : undefined },
        config                  : { type : Object, default : undefined },
        constrainTo             : { type : [HTMLElement, Widget, Rectangle], default : undefined },
        contentElementCls       : { type : [String, Object], default : undefined },
        defaultBindProperty     : { type : String, default : undefined },
        defaultFocus            : { type : Function, default : undefined },
        defaults                : { type : Object, default : undefined },
        dock                    : { type : [String, Object], default : undefined },
        draggable               : { type : [Boolean, Object], default : undefined },
        floating                : { type : Boolean, default : undefined },
        footer                  : { type : [Object, String], default : undefined },
        header                  : { type : [String, Boolean, Object], default : undefined },
        hideAnimation           : { type : [Boolean, Object], default : undefined },
        hideWhenEmpty           : { type : Boolean, default : undefined },
        htmlCls                 : { type : [String, Object], default : undefined },
        ignoreParentReadOnly    : { type : Boolean, default : undefined },
        itemCls                 : { type : String, default : undefined },
        lazyItems               : { type : [Object, Array], default : undefined },
        listeners               : { type : Object, default : undefined },
        localeClass             : { type : Function, default : undefined },
        localizable             : { type : Boolean, default : undefined },
        localizableProperties   : { type : Array, default : undefined },
        maskDefaults            : { type : Object, default : undefined },
        masked                  : { type : [Boolean, String, Object], default : undefined },
        monitorResize           : { type : Boolean, default : undefined },
        namedItems              : { type : Object, default : undefined },
        owner                   : { type : Widget, default : undefined },
        positioned              : { type : Boolean, default : undefined },
        preventTooltipOnTouch   : { type : Boolean, default : undefined },
        relayStoreEvents        : { type : Boolean, default : undefined },
        ripple                  : { type : [Boolean, Object], default : undefined },
        rootElement             : { type : [ShadowRoot, HTMLElement], default : undefined },
        scrollAction            : { type : [String, null], default : undefined },
        showAnimation           : { type : [Boolean, Object], default : undefined },
        showTooltipWhenDisabled : { type : Boolean, default : undefined },
        stateful                : { type : [Boolean, Object, Array], default : undefined },
        statefulEvents          : { type : [Object, Array], default : undefined },
        stateId                 : { type : String, default : undefined },
        stateProvider           : { type : StateProvider, default : undefined },
        strips                  : { type : Object, default : undefined },
        tab                     : { type : [Boolean, Object], default : undefined },
        tabMaxWidth             : { type : Number, default : undefined },
        tabMinWidth             : { type : Number, default : undefined },
        tag                     : { type : String, default : undefined },
        tbar                    : { type : [Array, Object], default : undefined },
        textAlign               : { type : String, default : undefined },
        textContent             : { type : Boolean, default : undefined },
        trapFocus               : { type : Boolean, default : undefined },
        ui                      : { type : [String, Object], default : undefined },
        weight                  : { type : Number, default : undefined },

         // Configs and properties
        activeTab           : { type : [Number, Widget], default : undefined },
        alignSelf           : { type : String, default : undefined },
        appendTo            : { type : [HTMLElement, String], default : undefined },
        ariaLive            : { type : String, default : undefined },
        cls                 : { type : [String, Object], default : undefined },
        content             : { type : String, default : undefined },
        dataset             : { type : Object, default : undefined },
        disabled            : { type : Boolean, default : undefined },
        extraData           : { type : [String, Number, Boolean, Object], default : undefined },
        flex                : { type : [Number, String], default : undefined },
        height              : { type : [Number, String], default : undefined },
        hidden              : { type : Boolean, default : undefined },
        html                : { type : Function, default : undefined },
        id                  : { type : String, default : undefined },
        insertBefore        : { type : [HTMLElement, String], default : undefined },
        insertFirst         : { type : [HTMLElement, String], default : undefined },
        items               : { type : [Array, Object], default : undefined },
        layout              : { type : [Layout, String, Object], default : undefined },
        layoutStyle         : { type : Object, default : undefined },
        margin              : { type : [Number, String], default : undefined },
        maxHeight           : { type : [String, Number], default : undefined },
        maximizeOnMobile    : { type : [Number, String], default : undefined },
        maxWidth            : { type : [String, Number], default : undefined },
        minHeight           : { type : [String, Number], default : undefined },
        minWidth            : { type : [String, Number], default : undefined },
        readOnly            : { type : Boolean, default : undefined },
        record              : { type : Model, default : undefined },
        scrollable          : { type : [Scroller, Boolean, Object], default : undefined },
        strictRecordMapping : { type : Boolean, default : undefined },
        title               : { type : String, default : undefined },
        tools               : { type : Object, default : undefined },
        tooltip             : { type : [String, Object], default : undefined },
        width               : { type : [Number, String], default : undefined },
        x                   : { type : Number, default : undefined },
        y                   : { type : Number, default : undefined },

         // Properties only
        accessibility   : { type : Boolean, default : undefined },
        anchorSize      : { type : Array, default : undefined },
        isSettingValues : { type : Boolean, default : undefined },
        isValid         : { type : Boolean, default : undefined },
        state           : { type : Object, default : undefined },
        values          : { type : Object, default : undefined },

         // Events
        onBeforeDestroy   : { type : Function },
        onBeforeHide      : { type : Function },
        onBeforeSetRecord : { type : Function },
        onBeforeShow      : { type : Function },
        onBeforeTabChange : { type : Function },
        onCatchAll        : { type : Function },
        onCollapse        : { type : Function },
        onDestroy         : { type : Function },
        onExpand          : { type : Function },
        onFocusIn         : { type : Function },
        onFocusOut        : { type : Function },
        onHide            : { type : Function },
        onPaint           : { type : Function },
        onReadOnly        : { type : Function },
        onRecompose       : { type : Function },
        onResize          : { type : Function },
        onShow            : { type : Function },
        onTabChange       : { type : Function },
        onToolClick       : { type : Function },

    },

    emits : [
        'beforedestroy',
        'beforehide',
        'beforesetrecord',
        'beforeshow',
        'beforetabchange',
        'catchall',
        'collapse',
        'destroy',
        'expand',
        'focusin',
        'focusout',
        'hide',
        'paint',
        'recompose',
        'resize',
        'show',
        'tabchange',
        'toolclick'
    ],

    setup(props, { emit }) {
        const instance = {};
        const refElement = ref(null);

        // Storage for teleports (in-cell Vue component instances)
        // automatically renderer by template
        const teleports = ref(new Map());

        const { createWidget, relayStores, watchProps } = useWrapperHelper();
        const { processCellContent, hasFrameworkRenderer } = useWrapperHelperVue3();

        // Provide teleports for processCellContent
        provide('teleports', teleports);

        onMounted(() => {
            const
                me = getCurrentInstance(),
                uncapitalize = str => str.charAt(0).toLowerCase() + str.slice(1),
                eventName = str => uncapitalize(str.slice(2)),
                // Collect event listeners from me.props where they are `on` functions
                listeners = Object.keys(me.props)
                    .filter(prop => typeof me.props[prop] === 'function' && prop.startsWith('on') && prop.length > 2)
                    .map(eventName);

            instance.value = createWidget({
                me,
                props   : me.props,
                data    : me.data,
                listeners,
                emit,
                element : refElement.value,
                toRaw
            });

            const watcher = (prop, newValue) => watch(() => me.props[prop], newValue);
            watchProps(me, instance.value, me.props, me.data, watcher);
        });

        onBeforeUnmount(() => {
            if (instance.value) {
                instance.value.destroy();
            }
        });

        return { instance, refElement, teleports };
    },

    data : () => {
        return {
            instanceClass : TabPanel,
            instanceName : 'TabPanel',

            configNames : [
                'adopt',
                'align',
                'anchor',
                'animateTabChange',
                'ariaDescription',
                'ariaLabel',
                'autoHeight',
                'autoUpdateRecord',
                'bbar',
                'bodyCls',
                'bubbleEvents',
                'callOnFunctions',
                'centered',
                'collapsed',
                'collapsible',
                'config',
                'constrainTo',
                'contentElementCls',
                'defaultBindProperty',
                'defaultFocus',
                'defaults',
                'dock',
                'draggable',
                'floating',
                'footer',
                'header',
                'hideAnimation',
                'hideWhenEmpty',
                'htmlCls',
                'ignoreParentReadOnly',
                'itemCls',
                'lazyItems',
                'listeners',
                'localeClass',
                'localizable',
                'localizableProperties',
                'maskDefaults',
                'masked',
                'monitorResize',
                'namedItems',
                'owner',
                'positioned',
                'preventTooltipOnTouch',
                'relayStoreEvents',
                'ripple',
                'rootElement',
                'scrollAction',
                'showAnimation',
                'showTooltipWhenDisabled',
                'stateful',
                'statefulEvents',
                'stateId',
                'stateProvider',
                'strips',
                'tab',
                'tabMaxWidth',
                'tabMinWidth',
                'tag',
                'tbar',
                'textAlign',
                'textContent',
                'trapFocus',
                'ui',
                'weight'
            ],

            propertyConfigNames : [
                'activeTab',
                'alignSelf',
                'appendTo',
                'ariaLive',
                'cls',
                'content',
                'dataset',
                'disabled',
                'extraData',
                'flex',
                'height',
                'hidden',
                'html',
                'id',
                'insertBefore',
                'insertFirst',
                'items',
                'layout',
                'layoutStyle',
                'margin',
                'maxHeight',
                'maximizeOnMobile',
                'maxWidth',
                'minHeight',
                'minWidth',
                'onBeforeDestroy',
                'onBeforeHide',
                'onBeforeSetRecord',
                'onBeforeShow',
                'onBeforeTabChange',
                'onCatchAll',
                'onCollapse',
                'onDestroy',
                'onExpand',
                'onFocusIn',
                'onFocusOut',
                'onHide',
                'onPaint',
                'onReadOnly',
                'onRecompose',
                'onResize',
                'onShow',
                'onTabChange',
                'onToolClick',
                'readOnly',
                'record',
                'scrollable',
                'strictRecordMapping',
                'title',
                'tools',
                'tooltip',
                'width',
                'x',
                'y'
            ],

            propertyNames : [
                'accessibility',
                'anchorSize',
                'isSettingValues',
                'isValid',
                'state',
                'values'
            ],

            eventNames : [
                'beforeDestroy',
                'beforeHide',
                'beforeSetRecord',
                'beforeShow',
                'beforeTabChange',
                'catchAll',
                'collapse',
                'destroy',
                'expand',
                'focusIn',
                'focusOut',
                'hide',
                'paint',
                'recompose',
                'resize',
                'show',
                'tabChange',
                'toolClick'
            ]
        };
    }
};
</script>
